<template>
  <div class="agent-upload-page">
    <div class="agent-upload-inner">
      <header class="page-header">
        <p class="eyebrow">Certificate review results</p>
        <h1>{{ headerTitle }}</h1>
        <p class="purpose-lead">{{ headerMessage }}</p>
        <p v-if="statusSummary" class="page-meta">{{ statusSummary }}</p>
      </header>

      <div v-if="loading" class="purpose-panel">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
        <div class="purpose-body">
          <h2>Loading results</h2>
          <p>Retrieving the analysis for your uploaded certificate.</p>
        </div>
      </div>

      <div v-else-if="error" class="error-panel">
        <h2>Could not load results</h2>
        <p>{{ error }}</p>
      </div>

      <template v-else>
        <!-- Processing error -->
        <section v-if="hasProcessingError" class="purpose-panel purpose-panel--alert">
          <div class="purpose-icon" aria-hidden="true">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div class="purpose-body">
            <h2>We could not complete this review</h2>
            <p v-if="processingErrorDetail">{{ processingErrorDetail }}</p>
            <p v-else>
              The document could not be fully analyzed. These are not compliance
              failures, and no certificate changes should be made based on this run.
            </p>
            <p v-if="!processingErrorDetail" class="processing-error-note">
              These are not compliance failures, and no certificate changes should
              be made based on this run.
            </p>
            <p class="next-action">
              <span class="next-label">Next:</span>
              {{ processingErrorNextAction }}
            </p>
            <button type="button" class="btn-primary" @click="uploadRevised">
              Upload the certificate again
            </button>
          </div>
        </section>

        <!-- Issues view -->
        <template v-else-if="hasActionableWork">
          <section
            v-if="agentSummary"
            class="purpose-panel purpose-panel--alert"
          >
            <div class="purpose-icon" aria-hidden="true">
              <i class="fas fa-clipboard-list"></i>
            </div>
            <div class="purpose-body">
              <h2>What you need to do</h2>
              <p v-if="agentSummary.agent_overview" class="overview-text">
                {{ agentSummary.agent_overview }}
              </p>
              <p class="next-action">
                <span class="next-label">Next:</span>
                Work through the items below, then upload an updated certificate or
                supporting endorsements.
              </p>
            </div>
          </section>

          <section v-else-if="loadingSummary" class="purpose-panel">
            <div class="purpose-icon" aria-hidden="true">
              <i class="fas fa-spinner fa-spin"></i>
            </div>
            <div class="purpose-body">
              <h2>Preparing your instructions</h2>
              <p>Summarizing the analysis into clear action items.</p>
            </div>
          </section>

          <section v-if="agentSummary" class="purpose-panel remediation-panel">
            <div class="purpose-icon" aria-hidden="true">
              <i class="fas fa-file-upload"></i>
            </div>
            <div class="purpose-body">
              <h2>What to upload</h2>
              <p>
                Upload an amended certificate, matching endorsement pages, or relevant
                policy/dec pages via the secure upload link you were sent.
              </p>
              <ul class="remediation-list">
                <li>Certificate alone is fine for limits, dates, and named insured fixes.</li>
                <li>Additional Insured / Waiver of Subrogation often need endorsement schedules.</li>
                <li>Exclusion questions may need policy forms — those PDFs are usually much longer than a COI.</li>
              </ul>
            </div>
          </section>

          <div v-if="groupedFixInstructions.length" class="policy-groups">
            <details
              v-for="(group, groupIdx) in groupedFixInstructions"
              :key="'fix-' + group.policy"
              class="policy-group"
              :open="groupIdx === 0"
            >
              <summary class="policy-group-summary">
                <span class="policy-group-icon" aria-hidden="true">
                  <i :class="policyIcon(group.policy)"></i>
                </span>
                <span class="policy-group-title">{{ group.policy }}</span>
                <span class="policy-group-count">
                  {{ group.items.length }} {{ group.items.length === 1 ? 'item' : 'items' }}
                </span>
                <i class="fas fa-chevron-down policy-group-chevron" aria-hidden="true"></i>
              </summary>

              <ol class="instructions-list">
                <li
                  v-for="entry in group.items"
                  :key="entry.idx"
                  :value="entry.displayNumber"
                >
                  <p class="instruction-text">{{ entry.item.action }}</p>
                  <div
                    v-if="entry.item.stores_to_confirm && entry.item.stores_to_confirm.length"
                    class="roster-confirm"
                  >
                    <button type="button" class="btn-link" @click="uploadRevised">
                      Yes, we insure those stores
                    </button>
                    <button
                      type="button"
                      class="btn-link"
                      :disabled="decliningStores"
                      @click="declineStores(entry.item.stores_to_confirm)"
                    >
                      We do not insure those stores
                    </button>
                    <p v-if="rosterDeclineError" class="paste-warning">{{ rosterDeclineError }}</p>
                  </div>

                  <details
                    v-if="entry.item.paste_blocks && entry.item.paste_blocks.length"
                    class="paste-disclosure"
                  >
                    <summary class="paste-disclosure-summary">
                      <i class="fas fa-clipboard" aria-hidden="true"></i>
                      Show paste text
                      ({{ entry.item.paste_blocks.length }}
                      {{ entry.item.paste_blocks.length === 1 ? 'field' : 'fields' }})
                    </summary>
                    <div class="paste-blocks">
                      <div
                        v-for="(block, bIdx) in entry.item.paste_blocks"
                        :key="bIdx"
                        class="paste-block"
                      >
                        <div class="paste-block-header">
                          <strong class="paste-field-label">{{ block.field_label || block.acord_field }}</strong>
                          <button
                            type="button"
                            class="copy-btn"
                            :class="{ 'copy-btn-success': copiedKey === entry.idx + ':' + bIdx }"
                            @click="copyPasteBlock(entry.item, block, entry.idx, bIdx)"
                          >
                            <i :class="copiedKey === entry.idx + ':' + bIdx ? 'fas fa-check' : 'fas fa-copy'"></i>
                            {{ copiedKey === entry.idx + ':' + bIdx ? 'Copied!' : 'Copy' }}
                          </button>
                        </div>
                        <pre class="paste-text">{{ block.text }}</pre>
                        <p v-if="block.note" class="paste-note">
                          <i class="fas fa-info-circle"></i> {{ block.note }}
                        </p>
                        <p v-if="block.truncation_warning" class="paste-warning">
                          <i class="fas fa-exclamation-triangle"></i> {{ block.truncation_warning }}
                        </p>
                      </div>
                    </div>
                  </details>
                </li>
              </ol>
            </details>
          </div>

          <section
            v-if="groupedConfirmOrAddInstructions.length"
            class="purpose-panel confirm-add-panel"
          >
            <div class="purpose-icon" aria-hidden="true">
              <i class="fas fa-question-circle"></i>
            </div>
            <div class="purpose-body">
              <h2>Coverage not found on this certificate</h2>
              <p>
                These policy lines do not appear on the certificate you submitted.
                If you do not offer one of them, say so in “Policies you do not provide” below.
              </p>

              <div class="policy-groups confirm-add-groups">
                <details
                  v-for="group in groupedConfirmOrAddInstructions"
                  :key="'confirm-' + group.policy"
                  class="policy-group"
                >
                  <summary class="policy-group-summary">
                    <span class="policy-group-icon" aria-hidden="true">
                      <i :class="policyIcon(group.policy)"></i>
                    </span>
                    <span class="policy-group-title">{{ group.policy }}</span>
                    <span class="policy-group-count">
                      {{ group.items.length }} {{ group.items.length === 1 ? 'item' : 'items' }}
                    </span>
                    <i class="fas fa-chevron-down policy-group-chevron" aria-hidden="true"></i>
                  </summary>

                  <ol class="instructions-list confirm-add-list">
                    <li
                      v-for="entry in group.items"
                      :key="entry.idx"
                      :value="entry.displayNumber"
                    >
                      <p class="instruction-text">{{ entry.item.action }}</p>
                    </li>
                  </ol>
                </details>
              </div>
            </div>
          </section>

          <div
            v-if="!agentSummary && !loadingSummary"
            class="fallback-actions"
          >
            <div
              v-for="(req, idx) in failedRequirements"
              :key="req.id"
              class="purpose-panel purpose-panel--alert fallback-card"
            >
              <div class="purpose-icon" aria-hidden="true">
                <span class="fallback-number">{{ idx + 1 }}</span>
              </div>
              <div class="purpose-body">
                <h2>{{ req.requirement_name || 'Requirement' }}</h2>
                <p>{{ getActionText(req) }}</p>
              </div>
            </div>
          </div>

          <div class="upload-actions">
            <button type="button" class="btn-primary" @click="uploadRevised">
              Upload updated certificate
            </button>
            <button type="button" class="btn-link" @click="submitAsIs">
              Submit as-is anyway
            </button>
          </div>

          <details class="details-disclosure">
            <summary class="details-summary">
              <i class="fas fa-search" aria-hidden="true"></i>
              View detailed analysis
            </summary>
            <div class="details-panel">
              <div
                v-for="req in detailFailedRequirements"
                :key="'detail-fail-' + req.id"
                class="detail-row"
              >
                <span class="detail-marker detail-marker--fail" aria-hidden="true">×</span>
                <div class="detail-content">
                  <strong>{{ req.requirement_name || 'Requirement' }}</strong>
                  <p v-if="getCleanEvidence(req)">{{ getCleanEvidence(req) }}</p>
                  <p v-if="getCleanReasoning(req)" class="detail-reasoning">{{ getCleanReasoning(req) }}</p>
                </div>
              </div>
              <div
                v-for="req in passedRequirements"
                :key="'detail-pass-' + req.id"
                class="detail-row"
              >
                <span class="detail-marker detail-marker--pass" aria-hidden="true">✓</span>
                <div class="detail-content">
                  <strong>{{ req.requirement_name || 'Requirement' }}</strong>
                  <p v-if="getPassedSummary(req)" class="passed-detail">{{ getPassedSummary(req) }}</p>
                </div>
              </div>
            </div>
          </details>
        </template>

        <!-- Success view -->
        <template v-else-if="passedCount > 0">
          <section class="purpose-panel purpose-panel--success">
            <div class="purpose-icon" aria-hidden="true">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="purpose-body">
              <h2>No issues found at this time</h2>
              <p>
                We do not see any issues on this certificate at this time.
                We may follow up if we identify anything later.
              </p>
              <ul v-if="requestUpdateInstructions.length" class="request-updates">
                <li v-for="(item, idx) in requestUpdateInstructions" :key="'request-' + idx">
                  {{ item.action }}
                </li>
              </ul>
              <p class="page-meta">{{ passedCount }} requirements verified</p>
            </div>
          </section>

          <div class="upload-actions upload-actions--secondary">
            <button type="button" class="btn-link" @click="uploadRevised">
              Upload another document
            </button>
          </div>
        </template>

        <section class="purpose-panel policy-disposition-panel">
          <div class="purpose-icon" aria-hidden="true">
            <i class="fas fa-comment-dots"></i>
          </div>
          <div class="purpose-body">
            <h2>Policies you do not provide</h2>
            <p>
              Tell the requesting team if you do not offer a policy on this review,
              or if a policy that currently meets the requirements is being cancelled
              or will not be renewed. You can do this whether the certificate passed
              or still has gaps. When we can match your agency to this insured, your
              selections update which policies we associate with you for future requests.
            </p>

            <div v-if="policyLines.length" class="policy-disposition-list">
              <div
                v-for="line in policyLines"
                :key="line.coverage_code"
                class="policy-disposition-row"
              >
                <div class="policy-disposition-heading">
                  <strong>{{ line.label }}</strong>
                  <span class="policy-status" :class="'policy-status--' + line.status">
                    {{ policyStatusLabel(line.status) }}
                  </span>
                </div>
                <label class="sr-only" :for="'disposition-' + line.coverage_code">
                  What should the requesting team know about {{ line.label }}?
                </label>
                <select
                  :id="'disposition-' + line.coverage_code"
                  v-model="line.disposition"
                  class="form-control"
                >
                  <option value="">Select what applies</option>
                  <option value="provided">We provide this policy</option>
                  <option value="not_offered">We do not offer this policy</option>
                  <option value="cancelling">This policy is being cancelled or will not renew</option>
                </select>
                <input
                  v-if="line.disposition"
                  v-model="line.note"
                  class="form-control"
                  type="text"
                  maxlength="1000"
                  placeholder="Optional detail, such as a cancellation date"
                />
              </div>
            </div>

            <div v-else class="policy-disposition-row">
              <label for="custom-policy-name">Name a policy you do not provide</label>
              <input
                id="custom-policy-name"
                v-model="customPolicy.label"
                class="form-control"
                type="text"
                maxlength="255"
                placeholder="For example, Umbrella"
              />
              <label class="sr-only" for="custom-policy-disposition">What is happening with that policy?</label>
              <select id="custom-policy-disposition" v-model="customPolicy.disposition" class="form-control">
                <option value="">Select what the requesting team should know</option>
                <option value="not_offered">We do not offer this policy</option>
                <option value="cancelling">This policy is being cancelled or will not renew</option>
              </select>
              <input
                v-if="customPolicy.disposition"
                v-model="customPolicy.note"
                class="form-control"
                type="text"
                maxlength="1000"
                placeholder="Optional detail, such as a cancellation date"
              />
            </div>

            <p v-if="dispositionError" class="disposition-error">{{ dispositionError }}</p>
            <p v-if="dispositionSaved" class="disposition-saved">
              {{ dispositionSavedMessage }}
            </p>
            <button
              type="button"
              class="btn-primary"
              :disabled="submittingDisposition || !canSendDispositions"
              @click="submitDispositions"
            >
              {{ submittingDisposition ? 'Sending...' : 'Send this to the requesting team' }}
            </button>
          </div>
        </section>

        <div class="upload-actions upload-actions--secondary">
          <button type="button" class="btn-link" @click="proceedToFeedback">
            Provide feedback
          </button>
        </div>

        <details v-if="checkRun && checkRun.document" class="document-disclosure">
          <summary class="document-summary">
            <i class="fas fa-file-pdf" aria-hidden="true"></i>
            Your document
            <a
              v-if="documentUrl"
              :href="documentUrl"
              target="_blank"
              class="view-full-link"
              @click.stop
            >
              View full document
            </a>
          </summary>
          <div class="document-preview">
            <div v-if="documentUrl && isPDF" class="pdf-container">
              <iframe
                :src="documentUrl"
                type="application/pdf"
                class="pdf-preview"
                title="Document Preview"
              ></iframe>
            </div>
            <div v-else class="no-preview">
              <i class="fas fa-file"></i>
              <p>Document uploaded successfully</p>
              <a v-if="documentUrl" :href="documentUrl" target="_blank" class="download-link">
                <i class="fas fa-download"></i> Download document
              </a>
            </div>
          </div>
        </details>
      </template>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'SecureUploadResults',
  data() {
    return {
      token: null,
      checkRunId: null,
      checkRun: null,
      documentUrl: '',
      documentName: '',
      loading: true,
      error: null,
      agentSummary: null,
      subjectPosture: null,
      remediationHint: null,
      loadingSummary: false,
      copiedKey: '',
      copiedTimeout: null,
      policyLines: [],
      customPolicy: { label: '', disposition: '', note: '' },
      submittingDisposition: false,
      dispositionError: '',
      dispositionSaved: false,
      dispositionSavedMessage: '',
      decliningStores: false,
      rosterDeclineError: '',
    };
  },

  computed: {
    passedRequirements() {
      if (!this.checkRun?.requirement_results) return [];
      return this.checkRun.requirement_results.filter(req => req.result === 'PASS');
    },

    failedRequirements() {
      if (!this.checkRun?.requirement_results) return [];
      return this.checkRun.requirement_results.filter(req => req.result === 'FAIL');
    },

    erroredRequirements() {
      if (!this.checkRun?.requirement_results) return [];
      return this.checkRun.requirement_results.filter(req => req.result === 'ERROR');
    },

    passedCount() { return this.passedRequirements.length; },
    rawFailedCount() { return this.failedRequirements.length; },
    failedCount() {
      const summaryCount = this.agentSummary?.failed_count;
      if (summaryCount !== undefined && summaryCount !== null) {
        return Number(summaryCount);
      }
      return this.rawFailedCount;
    },
    confirmOrAddCount() {
      const fromSummary = this.agentSummary?.confirm_or_add_count;
      if (fromSummary !== undefined && fromSummary !== null) {
        return Number(fromSummary);
      }
      return this.groupedConfirmOrAddInstructions.reduce(
        (n, g) => n + g.items.length,
        0,
      );
    },
    hasActionableWork() {
      return this.failedCount > 0 || this.confirmOrAddCount > 0;
    },
    errorCount() { return this.erroredRequirements.length; },

    hasProcessingError() {
      return (
        this.errorCount > 0
        || this.checkRun?.status === 'FAILED'
        || this.checkRun?.results_summary?.summary_status === 'PROCESSING_ERROR'
        || Number(this.checkRun?.run_metadata?.evaluation_errors || 0) > 0
      );
    },

    totalCount() { return this.checkRun?.requirement_results?.length || 0; },

    groupedFixInstructions() {
      const items = (this.agentSummary?.agent_instructions || []).filter(
        (item) => !item?.kind || item.kind === 'FIX'
      );
      return this._groupInstructionsByPolicy(items);
    },

    groupedConfirmOrAddInstructions() {
      const items = (this.agentSummary?.agent_instructions || []).filter(
        (item) => item?.kind === 'CONFIRM_OR_ADD'
      );
      return this._groupInstructionsByPolicy(items);
    },

    requestUpdateInstructions() {
      return (this.agentSummary?.agent_instructions || []).filter(
        (item) => item?.kind === 'REQUEST_UPDATE'
      );
    },

    headerTitle() {
      if (this.hasProcessingError) return 'Analysis could not complete';
      if (this.failedCount === 0 && this.passedCount > 0) return 'No issues at this time';
      if (this.failedCount > 0) return 'Updates needed';
      return 'Analysis complete';
    },

    acordSpatialReason() {
      return (
        this.checkRun?.results_summary?.acord_spatial_reason
        || this.checkRun?.run_metadata?.acord_spatial_reason
        || null
      );
    },

    processingErrorDetail() {
      const fromSummary = this.checkRun?.results_summary?.error;
      if (fromSummary && typeof fromSummary === 'string') {
        return fromSummary;
      }
      return '';
    },

    processingErrorNextAction() {
      if (this.acordSpatialReason === 'pairings_not_extractable') {
        return (
          'Ask the insured for a clean PDF export of the ACORD 25 (not a scan '
          + 'or photo), then upload it here.'
        );
      }
      if (
        this.acordSpatialReason === 'sidecar_unavailable'
        || this.acordSpatialReason === 'sidecar_busy'
      ) {
        return 'Wait a few minutes, then upload the certificate again.';
      }
      return 'Upload the certificate again so we can retry the review.';
    },

    headerMessage() {
      if (this.hasProcessingError) {
        return (
          this.processingErrorDetail
          || 'No compliance determination was made. Please upload the certificate again.'
        );
      }
      if (this.failedCount === 0 && this.passedCount > 0) {
        return 'We do not see any issues on this certificate at this time. We may follow up if we identify anything later.';
      }
      if (this.failedCount > 0) {
        return 'Review the items below and upload an updated certificate when ready.';
      }
      return 'Analysis completed.';
    },

    statusSummary() {
      if (this.hasProcessingError) return 'Analysis error — no pass/fail determination';
      const parts = [];
      if (this.failedCount > 0) parts.push(`${this.failedCount} to fix`);
      if (this.confirmOrAddCount > 0) {
        parts.push(`${this.confirmOrAddCount} to confirm`);
      }
      if (this.passedCount > 0) parts.push(`${this.passedCount} passed`);
      return parts.join(' · ');
    },

    suppressedCoverageCodes() {
      const resolution = this.agentSummary?.coverage_resolution || {};
      return Object.entries(resolution)
        .filter(([, status]) => String(status || '').startsWith('SUPPRESSED'))
        .map(([code]) => code);
    },

    detailFailedRequirements() {
      const suppressed = new Set(this.suppressedCoverageCodes);
      const filtered = this.failedRequirements.filter((req) => {
        const types = req.applicable_coverage_types || [];
        if (!types.length) return true;
        return !types.some((code) => suppressed.has(code));
      });
      if (this.agentSummary?.agent_instructions?.length) {
        const fixIds = new Set(
          (this.agentSummary.agent_instructions || [])
            .filter((item) => item?.kind === 'FIX' && item.requirement_result_id)
            .map((item) => item.requirement_result_id),
        );
        if (fixIds.size) {
          return filtered.filter((req) => fixIds.has(req.id) || !(req.applicable_coverage_types || []).length);
        }
      }
      return filtered;
    },

    canSendDispositions() {
      if (this.policyLines.length) {
        return this.policyLines.some(line => line.disposition);
      }
      return Boolean(this.customPolicy.label.trim() && this.customPolicy.disposition);
    },

    isPDF() {
      if (!this.checkRun?.document) return false;
      const name = this.checkRun.document.name || this.checkRun.document.file_name || '';
      return name.toLowerCase().endsWith('.pdf');
    },
  },

  async created() {
    this.token = this.$route.params.token;
    this.checkRunId = this.$route.query.checkRunId;
    await this.loadResults();
  },

  methods: {
    _groupInstructionsByPolicy(items) {
      if (!items.length) return [];

      const order = [];
      const groups = new Map();

      items.forEach((item, idx) => {
        const policy = (item && item.policy && String(item.policy).trim()) || 'General';
        if (!groups.has(policy)) {
          groups.set(policy, []);
          order.push(policy);
        }
        groups.get(policy).push({ item, idx, displayNumber: idx + 1 });
      });

      order.sort((a, b) => {
        if (a === 'General' && b !== 'General') return 1;
        if (b === 'General' && a !== 'General') return -1;
        return 0;
      });

      return order.map(policy => ({ policy, items: groups.get(policy) }));
    },

    async loadResults() {
      try {
        const params = {};
        if (this.checkRunId) params.checkRunId = this.checkRunId;
        const response = await api.get(`/secure-upload/${this.token}/results/`, { params });
        const data = response.data;

        if (data.status === 'success' && data.check_run) {
          this.checkRun = data.check_run;
          this.checkRunId = this.checkRunId || data.check_run.id;
          this.documentName = data.document_name || 'Document';
          this.subjectPosture = data.subject_posture || null;
          this.remediationHint = data.remediation_hint || null;
          this.policyLines = (data.policy_lines || []).map(line => ({
            coverage_code: line.coverage_code,
            label: line.label,
            status: line.status,
            disposition: line.disposition || '',
            note: line.note || '',
          }));
          if (this.checkRun.document) {
            this.documentUrl = this.getDocumentUrl(this.checkRun.document);
          }

          const needsSummary = !this.hasProcessingError && (
            this.rawFailedCount > 0
            || (data.policy_lines || []).some((line) => line.status === 'not_on_certificate')
          );
          const existing = data.check_run.results_summary;
          const PASTE_BLOCKS_VERSION = 5;
          if (needsSummary) {
            if (
              existing?.agent_instructions?.length
              && existing?.paste_blocks_version === PASTE_BLOCKS_VERSION
            ) {
              this.agentSummary = existing;
            } else {
              this.fetchAgentSummary();
            }
          } else if (existing?.agent_instructions?.length) {
            this.agentSummary = existing;
          }
        } else {
          this.error = 'Failed to load analysis results';
        }
      } catch (err) {
        console.error('Error loading results:', err);
        this.error = 'Failed to load analysis results';
      } finally {
        this.loading = false;
      }
    },

    async fetchAgentSummary() {
      this.loadingSummary = true;
      try {
        const response = await api.post(`/secure-upload/${this.token}/agent-summary/`, {
          check_run_id: this.checkRunId,
        });
        if (response.data.status === 'success' && response.data.summary) {
          this.agentSummary = response.data.summary;
        }
      } catch (err) {
        console.error('Error fetching agent summary:', err);
      } finally {
        this.loadingSummary = false;
      }
    },

    getDocumentUrl(document) {
      const { getDocumentUrl } = require('@/utils/documentUrlHelper');
      return getDocumentUrl(document);
    },

    cleanText(raw) {
      if (!raw) return '';
      let text = typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
      text = text.replace(/\\n/g, ' ');
      text = text.replace(/\n/g, ' ');
      text = text.replace(/<[^>]*>/g, '');
      text = text.replace(/\s{2,}/g, ' ');
      return text.trim();
    },

    parseEvidence(requirement) {
      if (!requirement) return {};
      const evidence = requirement.evidence;
      if (!evidence) return {};
      if (typeof evidence === 'string') {
        try { return JSON.parse(evidence); } catch { return {}; }
      }
      return evidence;
    },

    getActionText(requirement) {
      if (requirement.recommendations) {
        return this.cleanText(requirement.recommendations);
      }
      const parsed = this.parseEvidence(requirement);
      if (parsed.deficiencies && Array.isArray(parsed.deficiencies) && parsed.deficiencies.length) {
        return parsed.deficiencies.map(d => this.cleanText(d)).join(' ');
      }
      if (parsed.reasoning) return this.cleanText(parsed.reasoning);
      return 'This requirement was not met. Please provide an updated document.';
    },

    getCleanEvidence(requirement) {
      const parsed = this.parseEvidence(requirement);
      if (parsed.evidence) return this.cleanText(parsed.evidence);
      if (requirement.formatted_evidence) return this.cleanText(requirement.formatted_evidence);
      return '';
    },

    getCleanReasoning(requirement) {
      const parsed = this.parseEvidence(requirement);
      if (parsed.reasoning) return this.cleanText(parsed.reasoning);
      if (requirement.reasoning) return this.cleanText(requirement.reasoning);
      return '';
    },

    getPassedSummary(requirement) {
      const parsed = this.parseEvidence(requirement);
      if (parsed.reasoning) return this.cleanText(parsed.reasoning);
      return '';
    },

    policyStatusLabel(status) {
      const labels = {
        met: 'Currently meets requirements',
        needs_attention: 'Needs attention on this certificate',
        not_on_certificate: 'Not on this certificate',
        review_incomplete: 'Review did not finish',
      };
      return labels[status] || '';
    },

    async declineStores(storeNumbers) {
      if (this.decliningStores || !storeNumbers?.length) return;
      this.decliningStores = true;
      this.rosterDeclineError = '';
      try {
        const response = await api.post(`/secure-upload/${this.token}/roster-declines/`, {
          check_run_id: this.checkRunId,
          store_numbers: storeNumbers,
        });
        if (response.data?.summary) {
          this.agentSummary = response.data.summary;
          if (this.checkRun) {
            this.checkRun.results_summary = response.data.summary;
          }
        } else {
          await this.fetchAgentSummary();
        }
      } catch (err) {
        this.rosterDeclineError = err?.response?.data?.message
          || 'Could not record that. Please try again.';
      } finally {
        this.decliningStores = false;
      }
    },

    async submitDispositions() {
      if (!this.canSendDispositions || this.submittingDisposition) return;
      this.submittingDisposition = true;
      this.dispositionError = '';
      this.dispositionSaved = false;

      const policies = this.policyLines.length
        ? this.policyLines
          .filter(line => line.disposition)
          .map(line => ({
            coverage_code: line.coverage_code,
            disposition: line.disposition,
            note: line.note || '',
          }))
        : [{
          coverage_code: 'CUSTOM',
          policy_label: this.customPolicy.label.trim(),
          disposition: this.customPolicy.disposition,
          note: this.customPolicy.note || '',
        }];

      try {
        const response = await api.post(`/secure-upload/${this.token}/policy-dispositions/`, {
          check_run_id: this.checkRunId,
          policies,
        });
        this.dispositionSaved = true;
        this.dispositionSavedMessage = response.data?.message
          || 'Sent. The requesting team can see this on the company record.';
        if (response.data?.summary) {
          this.agentSummary = response.data.summary;
          if (this.checkRun) {
            this.checkRun.results_summary = response.data.summary;
          }
        } else {
          await this.fetchAgentSummary();
        }
      } catch (err) {
        const message = err?.response?.data?.message;
        this.dispositionError = message || 'Could not send this. Please try again.';
      } finally {
        this.submittingDisposition = false;
      }
    },

    policyIcon(policy) {
      const p = (policy || '').toLowerCase();
      if (p.includes('general liability')) return 'fas fa-shield-alt';
      if (p.includes('auto')) return 'fas fa-truck';
      if (p.includes('workers')) return 'fas fa-hard-hat';
      if (p.includes('umbrella') || p.includes('excess')) return 'fas fa-umbrella';
      if (p.includes('professional')) return 'fas fa-briefcase';
      return 'fas fa-file-contract';
    },

    proceedToFeedback() {
      this.$router.push({
        name: 'SecureUploadFeedback',
        params: { token: this.token },
        query: { checkRunId: this.checkRunId, passed: this.passedCount, failed: this.failedCount },
      });
    },

    uploadRevised() {
      this.$router.push({ name: 'SecureUploadLanding', params: { token: this.token } });
    },

    async copyPasteBlock(instruction, block, idx, bIdx) {
      const key = `${idx}:${bIdx}`;
      const text = block && block.text ? block.text : '';
      if (!text) return;

      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          copied = true;
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          copied = document.execCommand('copy');
          document.body.removeChild(ta);
        }
      } catch (err) {
        console.error('Clipboard write failed:', err);
      }

      if (copied) {
        this.copiedKey = key;
        if (this.copiedTimeout) clearTimeout(this.copiedTimeout);
        this.copiedTimeout = setTimeout(() => { this.copiedKey = ''; }, 1500);
      }

      this.recordPasteEvent(instruction, block).catch(err => {
        console.warn('Paste-event tracking failed (non-fatal):', err);
      });
    },

    async recordPasteEvent(instruction, block) {
      if (!this.token || !this.checkRunId) return;
      try {
        await api.post(`/secure-upload/${this.token}/paste-event/`, {
          check_run_id: this.checkRunId,
          requirement_result_id: instruction?.requirement_result_id || null,
          acord_field: block.acord_field || 'other',
          gap_type: instruction?.gap_type || '',
          paste_block_source: block.source || 'llm',
          text: block.text || '',
          truncation_warned: !!block.truncation_warning,
        });
      } catch (err) {
        // Silent — copy already succeeded for the user.
      }
    },

    async submitAsIs() {
      if (!confirm('Submit this document as-is? The certificate holder will be notified of the outstanding items.')) return;
      try {
        const response = await api.post(`/secure-upload/${this.token}/submit-final/`, {
          document_name: this.documentName,
          check_run_id: this.checkRunId,
          total_requirements: this.totalCount,
          passed_count: this.passedCount,
          failed_count: this.failedCount,
          submission_type: 'as_is',
          timestamp: new Date().toISOString(),
        });
        if (response.data.status === 'success') {
          this.$router.push({
            name: 'SecureUploadComplete',
            params: { token: this.token },
            query: { passed: this.passedCount, failed: this.failedCount, submitted: 'as_is' },
          });
        } else {
          alert('Failed to submit document. Please try again.');
        }
      } catch (err) {
        console.error('Error submitting document:', err);
        alert('Failed to submit document. Please try again.');
      }
    },
  },
};
</script>

<style scoped>
.agent-upload-page {
  min-height: 100vh;
  background: #f8fafc;
  color: #0f172a;
}

.agent-upload-inner {
  max-width: 40rem;
  margin: 0 auto;
  padding: 2rem 1.25rem 3rem;
}

.page-header {
  margin-bottom: 1.25rem;
}

.eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
}

.page-header h1 {
  margin: 0 0 0.45rem;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
}

.purpose-lead {
  margin: 0 0 0.4rem;
  font-size: 0.98rem;
  color: #334155;
  line-height: 1.5;
}

.page-meta {
  margin: 0;
  font-size: 0.9rem;
  color: #475569;
  font-weight: 600;
}

.purpose-panel {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.85rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem 1.1rem;
  margin-bottom: 0.85rem;
  align-items: start;
}

.purpose-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.55rem;
  background: #f1f5f9;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.purpose-panel--alert .purpose-icon {
  background: #fff7ed;
  color: #c2410c;
}

.purpose-panel--success .purpose-icon {
  background: #ecfdf5;
  color: #047857;
}

.purpose-body h2 {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.purpose-body p {
  margin: 0 0 0.45rem;
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.45;
}

.purpose-body p:last-child {
  margin-bottom: 0;
}

.overview-text {
  color: #0f172a !important;
  font-weight: 500;
}

.next-action {
  color: #334155 !important;
}

.next-label {
  font-weight: 700;
  color: #0f172a;
}

.remediation-list {
  margin: 0.35rem 0 0;
  padding-left: 1.15rem;
  font-size: 0.88rem;
  color: #475569;
}

.remediation-list li {
  margin-bottom: 0.25rem;
}

.error-panel {
  background: #fff;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
  padding: 1rem 1.1rem;
  color: #991b1b;
}

.error-panel h2 {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
}

.error-panel p {
  margin: 0;
  font-size: 0.9rem;
}

.policy-groups {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
}

.policy-group {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  overflow: hidden;
}

.policy-group-summary {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  list-style: none;
  user-select: none;
}

.policy-group-summary::-webkit-details-marker {
  display: none;
}

.policy-group-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: #f1f5f9;
  color: var(--p-accent, #1e3a5f);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.policy-group-title {
  flex: 1;
  min-width: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
}

.policy-group-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  white-space: nowrap;
}

.policy-group-chevron {
  color: #94a3b8;
  font-size: 0.75rem;
  transition: transform 0.15s ease;
}

.policy-group[open] .policy-group-chevron {
  transform: rotate(180deg);
}

.instructions-list {
  margin: 0;
  padding: 0 1rem 0.75rem 2.2rem;
  border-top: 1px solid #f1f5f9;
}

.instructions-list li {
  padding: 0.7rem 0;
  font-size: 0.95rem;
  line-height: 1.55;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
}

.instructions-list li:last-child {
  border-bottom: none;
}

.instruction-text {
  font-weight: 500;
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-break: break-word;
}

.paste-disclosure {
  margin-top: 0.65rem;
}

.paste-disclosure-summary {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--p-accent, #1e3a5f);
  cursor: pointer;
  list-style: none;
}

.paste-disclosure-summary::-webkit-details-marker {
  display: none;
}

.paste-blocks {
  margin-top: 0.55rem;
  padding: 0.75rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
}

.paste-block {
  margin-bottom: 0.55rem;
}

.paste-block:last-child {
  margin-bottom: 0;
}

.paste-block-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.45rem;
}

.paste-field-label {
  font-size: 0.85rem;
  color: #1e293b;
  font-weight: 600;
}

.copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #fff;
  border: 1px solid #cbd5e1;
  color: #334155;
  padding: 0.3rem 0.65rem;
  border-radius: 0.375rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.copy-btn:hover {
  background: #f1f5f9;
}

.copy-btn-success {
  background: #ecfdf5;
  border-color: #86efac;
  color: #047857;
}

.paste-text {
  background: #0f172a;
  color: #e2e8f0;
  padding: 0.65rem 0.8rem;
  border-radius: 0.375rem;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.8rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  margin: 0;
}

.paste-note,
.paste-warning {
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  color: #475569;
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
}

.paste-warning {
  padding: 0.45rem 0.6rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 0.375rem;
}

.confirm-add-panel .policy-groups {
  margin-top: 0.75rem;
  margin-bottom: 0;
}

.fallback-actions {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
}

.fallback-number {
  font-weight: 700;
  font-size: 0.95rem;
}

.policy-disposition-panel {
  margin-top: 1rem;
}

.policy-disposition-list,
.policy-disposition-row {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin: 0.75rem 0;
}

.policy-disposition-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
}

.policy-status {
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
}

.policy-status--needs_attention,
.policy-status--not_on_certificate {
  color: #9a3412;
}

.policy-status--met {
  color: #166534;
}

.policy-disposition-row .form-control,
.policy-disposition-list .form-control {
  width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.4rem;
  font: inherit;
  background: #fff;
}

.disposition-error {
  margin: 0 0 0.5rem;
  color: #9f1239;
}

.disposition-saved {
  margin: 0 0 0.5rem;
  color: #166534;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.upload-actions {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin: 1rem 0 0.85rem;
}

.upload-actions--secondary {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.7rem 1rem;
  background: var(--p-accent, #1e3a5f);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-link {
  background: none;
  border: none;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.35rem;
  text-align: center;
}

.btn-link:hover {
  color: var(--p-accent, #1e3a5f);
}

.action-divider {
  color: #cbd5e1;
}

.details-disclosure,
.document-disclosure {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  margin-bottom: 0.85rem;
  overflow: hidden;
}

.details-summary,
.document-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  list-style: none;
}

.details-summary::-webkit-details-marker,
.document-summary::-webkit-details-marker {
  display: none;
}

.document-summary {
  justify-content: space-between;
  flex-wrap: wrap;
}

.view-full-link {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--p-accent, #1e3a5f);
  text-decoration: none;
  margin-left: auto;
}

.view-full-link:hover {
  text-decoration: underline;
}

.details-panel {
  border-top: 1px solid #e2e8f0;
  padding: 0.35rem 0;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.65rem 1rem;
  font-size: 0.88rem;
  border-bottom: 1px solid #f8fafc;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-marker {
  flex-shrink: 0;
  width: 1.1rem;
  font-weight: 700;
  text-align: center;
  margin-top: 0.1rem;
}

.detail-marker--fail {
  color: #b91c1c;
}

.detail-marker--pass {
  color: #047857;
}

.detail-content strong {
  display: block;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.2rem;
}

.detail-content p {
  margin: 0;
  line-height: 1.45;
  color: #475569;
  font-size: 0.85rem;
}

.detail-reasoning {
  font-style: italic;
  color: #64748b !important;
}

.passed-detail {
  color: #64748b;
}

.pdf-preview {
  width: 100%;
  height: 420px;
  border: none;
  display: block;
}

.no-preview {
  text-align: center;
  padding: 2rem 1rem;
  color: #64748b;
}

.no-preview i {
  font-size: 2rem;
  margin-bottom: 0.75rem;
  display: block;
}

.download-link {
  color: var(--p-accent, #1e3a5f);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.75rem;
  font-weight: 600;
  font-size: 0.875rem;
}

@media (max-width: 640px) {
  .agent-upload-inner {
    padding: 1.5rem 1rem 2.5rem;
  }

  .page-header h1 {
    font-size: 1.45rem;
  }

  .instructions-list {
    padding-left: 1.5rem;
  }

  .pdf-preview {
    height: 300px;
  }

  .upload-actions--secondary {
    flex-direction: column;
  }

  .action-divider {
    display: none;
  }
}
</style>
